import { useEffect, useState } from "react"
import { usePageContext } from "vike-react/usePageContext"

/**
 * URLSearchParams for the current page. SSR uses pageContext.urlParsed; the
 * client follows window.location after pushState/popstate so in-page updates
 * (sort, page) re-render without a full navigation.
 */
export default function useSearchParams() {
  const pageContext = usePageContext()
  const ssrSearch = pageContext?.urlParsed?.searchOriginal || ""
  const [search, setSearch] = useState(ssrSearch)

  useEffect(() => {
    const read = () => setSearch(window.location.search)
    read()
    window.addEventListener("popstate", read)
    window.addEventListener("sf-searchchange", read)
    return () => {
      window.removeEventListener("popstate", read)
      window.removeEventListener("sf-searchchange", read)
    }
  }, [pageContext?.urlPathname, ssrSearch])

  return new URLSearchParams(search)
}

// Update query params in place (no navigation) and notify useSearchParams consumers
export function setSearchParams(update, { replace = false } = {}) {
  if (typeof window === "undefined") return
  const params = new URLSearchParams(window.location.search)
  for (const [key, value] of Object.entries(update)) {
    if (value === null || value === undefined || value === "") params.delete(key)
    else params.set(key, String(value))
  }
  const qs = params.toString()
  const url = window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash
  window.history[replace ? "replaceState" : "pushState"](window.history.state || {}, "", url)
  window.dispatchEvent(new Event("sf-searchchange"))
}
